import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DashboardRole,
  getCouponWindfallCohort,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  WindfallCohortBucket,
  WindfallCohortRow,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { fmtPercent } from '../utils/dashboard.utils';
import { CenterLoading, ErrorBox, StatCard } from './DashboardShared';

/**
 * 낙전 코호트 — 판매월별로 이후 월마다 발송/낙전환불로 몇 건이 소진되고
 * 잔존(=낙전 예상)이 어떻게 줄어드는지 보여준다. 쿠폰 전용.
 * 스펙: docs/gift-coupon-windfall-cohort-spec.md
 */

const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

const fmtWon = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const fmtMan = (n: number) => `${Math.round(n / 10_000).toLocaleString('ko-KR')}만`;

type Axis = 'count' | 'amount';
type RangeMonths = 6 | 12 | 24 | 0; // 0 = 전체

// 매트릭스 열은 +0~+13 고정(만료월 +12 뒤에 처리되는 지연 환불까지 한 칸 여유).
// 아직 안 지난 달도 미리 영역을 잡아두고(미도래 ·) 가로 스크롤로 본다.
const MATRIX_OFFSETS = 14;

const RANGE_OPTIONS: Array<{ value: RangeMonths; label: string }> = [
  { value: 6, label: '최근 6개월' },
  { value: 12, label: '최근 12개월' },
  { value: 24, label: '최근 24개월' },
  { value: 0, label: '전체' },
];

/** KST 기준 현재 연·월 */
function kstNowParts(): { y: number; m: number } {
  const t = new Date(Date.now() + 9 * 3_600_000);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
}

/**
 * 최근 N개월(현재 월 포함) 판매월 범위를 KST 경계로 계산해 UTC(Z) ISO로 변환.
 * queryString이 encode:false라 '+09:00'을 그대로 보내면 서버가 '+'를 공백으로
 * 디코딩해 Invalid Date가 된다 — 반드시 toISOString()으로 변환해 보낼 것.
 */
function buildCohortRange(months: number): { from: string; to: string } {
  const { y, m } = kstNowParts();
  const startTotal = y * 12 + (m - 1) - (months - 1);
  const sy = Math.floor(startTotal / 12);
  const sm = (startTotal % 12) + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    from: new Date(`${sy}-${pad(sm)}-01T00:00:00+09:00`).toISOString(),
    to: new Date(`${y}-${pad(m)}-${pad(lastDay)}T23:59:59.999+09:00`).toISOString(),
  };
}

/** 'YYYY-MM' + n개월 */
function addMonthKey(key: string, n: number): string {
  const [y, m] = key.split('-').map(Number);
  const total = (y ?? 0) * 12 + ((m ?? 1) - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/** 'YYYY-MM' → 그 월말 일자 라벨 'YY.MM.DD' (매트릭스 셀 시점 표기) */
function monthEndLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const yy = y ?? 0;
  const mm = m ?? 1;
  const lastDay = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  return `${String(yy).slice(2)}.${String(mm).padStart(2, '0')}.${String(
    lastDay,
  ).padStart(2, '0')}`;
}

function remainRatio(
  row: WindfallCohortRow,
  b: WindfallCohortBucket,
  axis: Axis,
): number {
  const den = axis === 'count' ? row.total : row.totalAmount;
  const num = axis === 'count' ? b.remaining : b.remainingAmount;
  return den > 0 ? num / den : 0;
}

export const DashboardWindfallCohortSection: FC<{ role?: DashboardRole }> = ({
  role,
}) => {
  const [rows, setRows] = useState<WindfallCohortRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);
  const [rangeMonths, setRangeMonths] = useState<RangeMonths>(12);
  const [axis, setAxis] = useState<Axis>('count');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      const range = rangeMonths > 0 ? buildCohortRange(rangeMonths) : null;
      const res = await getCouponWindfallCohort(range?.from, range?.to, role);
      const cohorts = res.cohorts ?? [];
      setRows(cohorts);
      // 기본 선택: 가장 최근 판매월
      setSelectedMonth(cohorts[cohorts.length - 1]?.cohortMonth ?? null);
    } catch (e: unknown) {
      if (isNotFound(e)) {
        setNotReady(true);
      } else {
        setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      }
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [rangeMonths, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selected = useMemo(
    () => (rows ?? []).find((r) => r.cohortMonth === selectedMonth) ?? null,
    [rows, selectedMonth],
  );

  return (
    <section className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">월별 코호트 추이 (쿠폰)</h2>
          <p className="mt-1 text-xs text-gray-400">
            판매월별로 이후 월마다 발송·낙전환불로 몇 건이 소진되고, 잔존(=낙전 예상)이
            어떻게 줄어드는지 봅니다. 취소·폐기·추가발행분은 모수에서 제외됩니다.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setRangeMonths(o.value)}
                className={`rounded px-3 py-1 transition-colors ${
                  rangeMonths === o.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {(['count', 'amount'] as const).map((a) => (
              <button
                key={a}
                onClick={() => setAxis(a)}
                className={`rounded px-3 py-1 transition-colors ${
                  axis === a
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {a === 'count' ? '건수' : '금액'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {notReady && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-700">
          낙전 코호트 API가 아직 이 서버에 배포되지 않았습니다. 백엔드 배포 후 이용할 수
          있습니다.
        </div>
      )}
      {loading && <CenterLoading />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {!loading && !error && !notReady && rows && rows.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-400">
          해당 기간에 판매된 쿠폰이 없습니다.
        </div>
      )}

      {!loading && !error && !notReady && rows && rows.length > 0 && (
        <>
          {/* 코호트 매트릭스 */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500">
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold">
                    판매월
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">모수</th>
                  <th className="px-3 py-2 text-right font-semibold">현재 잔존</th>
                  {Array.from({ length: MATRIX_OFFSETS }, (_, i) => (
                    <th key={i} className="px-2 py-2 text-center font-semibold">
                      +{i}개월
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSelected = r.cohortMonth === selectedMonth;
                  const currentRatio =
                    (axis === 'count' ? r.total : r.totalAmount) > 0
                      ? (axis === 'count' ? r.remaining : r.remainingAmount) /
                        (axis === 'count' ? r.total : r.totalAmount)
                      : 0;
                  return (
                    <tr
                      key={r.cohortMonth}
                      onClick={() => setSelectedMonth(r.cohortMonth)}
                      className={`cursor-pointer border-t border-gray-100 text-sm transition-colors ${
                        isSelected ? 'bg-indigo-50/60' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td
                        className={`sticky left-0 z-10 whitespace-nowrap px-3 py-2 font-semibold ${
                          isSelected
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-white text-gray-700'
                        }`}
                      >
                        {r.cohortMonth}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-gray-700">
                        {axis === 'count'
                          ? `${r.total.toLocaleString('ko-KR')}건`
                          : fmtMan(r.totalAmount)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-amber-700">
                        {axis === 'count'
                          ? `${r.remaining.toLocaleString('ko-KR')}건`
                          : fmtMan(r.remainingAmount)}
                        <span className="ml-1 text-xs font-normal text-amber-500">
                          {fmtPercent(currentRatio)}
                        </span>
                      </td>
                      {Array.from({ length: MATRIX_OFFSETS }, (_, i) => {
                        const b = r.buckets[i];
                        // 판매월이 행마다 달라 헤더에 못 박는 실제 시점(월말 일자) — 셀 위에 표기
                        const cellDateLabel = monthEndLabel(
                          addMonthKey(r.cohortMonth, i),
                        );
                        if (!b) {
                          // 미도래(아직 안 지난 달) — 영역만 미리 잡아둔다
                          return (
                            <td key={i} className="bg-gray-50/60 px-2 py-2 text-center">
                              <div className="text-[10px] text-gray-400">
                                {cellDateLabel}
                              </div>
                              <div className="text-xs text-gray-300">·</div>
                            </td>
                          );
                        }
                        const ratio = remainRatio(r, b, axis);
                        const isExpiry = b.month === r.expiresMonth;
                        const prev = i === 0 ? undefined : r.buckets[i - 1];
                        // 소진 분해: 발송 vs 낙전환불. 금액 축의 환불분은
                        // 잔존액 차감분(판매가 축)이라 발송%+환불% = 그 달 잔존율 감소분
                        const den = axis === 'count' ? r.total : r.totalAmount;
                        const deliveredOut =
                          axis === 'count' ? b.delivered : b.deliveredAmount;
                        const refundedOut =
                          axis === 'count'
                            ? b.refunded
                            : Math.max(
                                0,
                                (prev?.remainingAmount ?? r.totalAmount) -
                                  b.remainingAmount -
                                  b.deliveredAmount,
                              );
                        const deliveredPct = den > 0 ? deliveredOut / den : 0;
                        const refundedPct = den > 0 ? refundedOut / den : 0;
                        return (
                          <td
                            key={i}
                            title={`${b.month} — 발송 ${b.delivered}건 · 낙전환불 ${b.refunded}건 · 월말 잔존 ${b.remaining}건`}
                            className={`whitespace-nowrap px-2 py-2 text-center ${
                              isExpiry ? 'ring-1 ring-inset ring-red-200' : ''
                            }`}
                            style={{
                              backgroundColor: `rgba(129, 140, 248, ${(
                                ratio * 0.55
                              ).toFixed(3)})`,
                            }}
                          >
                            <div className="text-[10px] font-semibold text-gray-800">
                              {cellDateLabel}
                            </div>
                            <div className="flex items-center justify-center gap-1 text-xs font-bold">
                              {deliveredOut > 0 && (
                                <span className="text-emerald-600">
                                  ▼{fmtPercent(deliveredPct)}
                                </span>
                              )}
                              {refundedOut > 0 && (
                                <span className="text-amber-600">
                                  ▼{fmtPercent(refundedPct)}
                                </span>
                              )}
                              {deliveredOut === 0 && refundedOut === 0 && (
                                <span className="font-normal text-gray-300">·</span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-600">
                              {axis === 'count'
                                ? b.remaining.toLocaleString('ko-KR')
                                : fmtMan(b.remainingAmount)}
                              <span className="ml-0.5 text-gray-400">
                                ({fmtPercent(ratio)})
                              </span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
            <span>
              셀 상단 <span className="font-semibold text-gray-800">날짜</span> = 그 월말
              스냅샷 시점, 가운데 = 그 달 빠진 비율(모수 대비,{' '}
              <span className="font-bold text-emerald-600">▼발송</span>{' '}
              <span className="font-bold text-amber-600">▼낙전환불</span>), 아래 = 월말
              잔존(잔존율). 배경이 짙을수록 잔존↑, 붉은 테두리 = 유효기간 만료월(구매
              +1년), · = 미도래. 행을 클릭하면 아래에 월별 상세가 열립니다.
            </span>
          </div>

          {/* 선택 코호트 상세 */}
          {selected && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                {selected.cohortMonth} 판매분 상세
                <span className="ml-2 text-xs font-normal text-gray-400">
                  유효기간 만료: {selected.expiresMonth}
                </span>
              </h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <StatCard
                  label="모수 (판매)"
                  value={`${selected.total.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.totalAmount)}
                  accent="indigo"
                />
                <StatCard
                  label="발송(사용) 누적"
                  value={`${selected.deliveredTotal.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.deliveredTotalAmount)}
                  accent="emerald"
                />
                <StatCard
                  label="낙전환불 누적"
                  value={`${selected.refundedTotal.toLocaleString('ko-KR')}건`}
                  sub={`실환불 ${fmtWon(selected.refundedTotalAmount)}`}
                  accent="red"
                />
                <StatCard
                  label="현재 잔존 (낙전 예상)"
                  value={`${selected.remaining.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.remainingAmount)}
                  accent="orange"
                />
              </div>
              {selected.refundedAfterDelivery > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  발송 후 환불 {selected.refundedAfterDelivery}건 (실환불{' '}
                  {fmtWon(selected.refundedAfterDeliveryAmount)}) — 발송 누적에 이미
                  포함되어 있어 잔존에서 재차감하지 않습니다.
                </p>
              )}

              <CohortDecayChart row={selected} />
            </div>
          )}
        </>
      )}
    </section>
  );
};

/** 월별 소진 스택 막대 — 아래부터 발송 누적(초록) / 낙전환불 누적(호박) / 잔존(남색) */
const CohortDecayChart: FC<{ row: WindfallCohortRow }> = ({ row }) => {
  if (row.total === 0 || row.buckets.length === 0) return null;

  const BAR_AREA_HEIGHT = 170;
  const barWidth = Math.min(Math.max(760 / row.buckets.length, 28), 64);

  let cumDelivered = 0;
  let cumRefunded = 0;
  const bars = row.buckets.map((b) => {
    cumDelivered += b.delivered;
    cumRefunded += b.refunded;
    return { bucket: b, cumDelivered, cumRefunded };
  });

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="overflow-x-auto">
        {/* 위 여백은 hover 툴팁 자리 — overflow-x-auto가 세로도 잘라서 공간을 미리 확보한다 */}
        <div
          className="flex w-max min-w-full items-end justify-center gap-1"
          style={{ height: BAR_AREA_HEIGHT + 76 }}
        >
          {bars.map(({ bucket: b, cumDelivered: cd, cumRefunded: cr }, i) => {
            const hDelivered = (cd / row.total) * BAR_AREA_HEIGHT;
            const hRefunded = (cr / row.total) * BAR_AREA_HEIGHT;
            const hRemaining = (b.remaining / row.total) * BAR_AREA_HEIGHT;
            const isExpiry = b.month === row.expiresMonth;
            // 가장자리 막대는 가운데 정렬 툴팁이 스크롤 영역 밖으로 잘려서 정렬을 바꾼다
            const tooltipAlign =
              i < bars.length / 3
                ? 'left-0'
                : i >= (bars.length * 2) / 3
                ? 'right-0'
                : 'left-1/2 -translate-x-1/2';
            return (
              <div
                key={b.month}
                className="group relative flex flex-col items-center justify-end"
                style={{ width: barWidth }}
              >
                <div
                  className={`pointer-events-none absolute bottom-full z-10 mb-1 hidden whitespace-nowrap rounded bg-gray-800 px-2.5 py-1 text-xs text-white shadow group-hover:block ${tooltipAlign}`}
                >
                  {b.month} — 발송 {b.delivered}건 · 낙전환불 {b.refunded}건 · 잔존{' '}
                  {b.remaining}건 ({fmtPercent(b.remaining / row.total)})
                </div>
                <span className="mb-0.5 text-[11px] font-semibold text-indigo-600">
                  {b.remaining}
                </span>
                <div
                  className="w-full rounded-t-sm bg-indigo-300"
                  style={{ height: Math.max(hRemaining, b.remaining > 0 ? 2 : 0) }}
                />
                <div
                  className="w-full bg-amber-400"
                  style={{ height: Math.max(hRefunded, cr > 0 ? 2 : 0) }}
                />
                <div
                  className="w-full rounded-b-sm bg-emerald-500"
                  style={{ height: Math.max(hDelivered, cd > 0 ? 2 : 0) }}
                />
                <span
                  className={`mt-1 text-[11px] ${
                    isExpiry ? 'font-bold text-red-500' : 'text-gray-400'
                  }`}
                >
                  {b.month.slice(2)}
                  {isExpiry ? ' 만료' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-medium">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <span className="text-gray-600">발송(사용) 누적</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="text-gray-600">낙전환불 누적</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-300" />
          <span className="text-gray-600">월말 잔존 (낙전 예상)</span>
        </span>
        <span className="ml-auto text-gray-400">막대 높이 = 모수 대비 비율</span>
      </div>
    </div>
  );
};
